import { api } from "@/lib/backend";
import { Capacitor } from "@/lib/capacitor";
import { Device } from "@/lib/capacitor/device";
import { PushNotifications } from "@/lib/capacitor/push-notifications";
import { logger } from "@/logger";

// LocalStorage key for push notification prompt dismissal
const PUSH_PROMPT_DISMISSED_KEY = "push-notification-prompt-dismissed-at";

// Number of days before showing the prompt again after dismissal
const DISMISSAL_DAYS = 7;

/**
 * Cleanup device registration from backend and unregister from push notifications Should be called
 * on logout to prevent notifications being sent to wrong user
 */
export async function cleanupDeviceRegistration() {
  if (Capacitor.Platform === "web") {
    logger.debug("Skipping device cleanup on web platform");
    return;
  }

  try {
    logger.info("Cleaning up device registration...");

    // Get current device identifier
    const deviceIdentifier = await Device.getDeviceIdentifier();

    // Unregister from push notifications (clears local state)
    await PushNotifications.unregister();
    logger.debug("Unregistered from push notifications locally");

    // Remove device from backend
    await api.notification.deleteDevice(deviceIdentifier);
    logger.info("Device registration cleaned up successfully", { deviceIdentifier });
  } catch (error) {
    // Don't throw - logout should proceed even if cleanup fails
    logger.error({ error }, "Failed to cleanup device registration");
  }
}

/** Register current device for push notifications Should be called after login and permission grant */
export async function registerDevice() {
  if (Capacitor.Platform === "web") {
    logger.debug("Skipping device registration on web platform");
    return;
  }

  try {
    logger.info("Registering device for push notifications...");

    // This will trigger the registration listener in NotificationListenerPush
    // which handles the backend API call
    await PushNotifications.register();

    logger.info("Device registration initiated");
  } catch (error) {
    logger.error({ error }, "Failed to register device");
    throw error;
  }
}

/**
 * Get the timestamp when the push notification prompt was last dismissed
 *
 * @returns Timestamp in milliseconds, or null if never dismissed
 */
export function getPushPromptDismissalTimestamp(): number | null {
  try {
    const dismissed = localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY);
    return dismissed ? parseInt(dismissed, 10) : null;
  } catch (error) {
    logger.error({ error }, "Failed to get push prompt dismissal timestamp");
    return null;
  }
}

/** Mark the push notification prompt as dismissed Stores the current timestamp in localStorage */
export function markPushPromptDismissed(): void {
  try {
    localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, Date.now().toString());
    logger.debug("Push notification prompt marked as dismissed");
  } catch (error) {
    logger.error({ error }, "Failed to mark push prompt as dismissed");
  }
}

/**
 * Reset the push notification prompt dismissal Call this when user manually navigates to
 * notification settings
 */
export function resetPushPromptDismissal(): void {
  try {
    localStorage.removeItem(PUSH_PROMPT_DISMISSED_KEY);
    logger.debug("Push notification prompt dismissal reset");
  } catch (error) {
    logger.error({ error }, "Failed to reset push prompt dismissal");
  }
}

/**
 * Check if the push notification prompt should be shown
 *
 * @param permissionState Current permission state from Capacitor
 * @param isDeviceRegistered Whether the current device is registered for push notifications
 * @returns True if prompt should be shown, false otherwise
 */
export function shouldShowPushPrompt(
  permissionState: string | null | undefined,
  isDeviceRegistered?: boolean,
): boolean {
  // Don't show on web (push not supported)
  if (Capacitor.Platform === "web") {
    return false;
  }

  // Always show if notifications are not fully active (permission granted AND device registered)
  const isFullyActive = permissionState === "granted" && isDeviceRegistered === true;
  if (!isFullyActive) {
    return true;
  }

  return false;
}
